import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Alert, Button, Input } from './ui';
import {
  AuthLayout, AuthHeading, PasswordInput, PasswordStrength, FieldNote, PASSWORD_MIN, cleanMessage,
  markOnboardingPending, clearOnboardingPending
} from './AuthShell';
import './Login.css';

const ASIDE = {
  art: 'profile',
  title: 'Аккаунт за минуту — и сразу к словам',
  points: ['Бесплатно, без карты', 'Настроим уровень и цель дня за 5 шагов', 'Слова и прогресс сохраняются в аккаунте']
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Register = () => {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const validate = () => {
    const e = {};
    if (username.trim().length < 3) e.username = 'Логин — не короче 3 символов';
    if (email.trim() && !EMAIL_RE.test(email.trim())) e.email = 'Проверьте email: например, name@mail.ru';
    if (password.length < PASSWORD_MIN) e.password = `Пароль — не короче ${PASSWORD_MIN} символов`;
    if (!confirm) e.confirm = 'Повторите пароль';
    else if (password !== confirm) e.confirm = 'Пароли не совпадают';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      const first = ['username', 'email', 'password', 'confirm'].find((k) => e[k]);
      document.getElementById(`reg-${first}`)?.focus();
      return;
    }
    setLoading(true);
    // До запроса: после успешной регистрации App.js сам уведёт с /register на /onboarding
    markOnboardingPending(name.trim());
    const result = await register(username.trim(), password, email.trim() || null);
    if (!result.success) {
      clearOnboardingPending();
      setLoading(false);
      const msg = result.message || '';
      // Сервер отвечает текстом; привязываем известные ошибки к полям
      if (/логин/i.test(msg) && /существует/i.test(msg)) setErrors({ username: 'Этот логин уже занят — выберите другой' });
      else if (/email/i.test(msg) && /зарегистрирован/i.test(msg)) setErrors({ email: 'Этот email уже зарегистрирован. Войдите или восстановите пароль.' });
      else setFormError(cleanMessage(msg, 'Не удалось создать аккаунт. Попробуйте ещё раз через минуту.'));
      return;
    }
    // Имя — необязательное; сохраняем отдельно, ошибка не мешает регистрации
    const displayName = name.trim();
    if (displayName) axios.put('/api/auth/profile', { displayName }).catch(() => {});
    // Переход на /onboarding делает маршрут /register в App.js (см. markOnboardingPending)
  };

  const clear = (key) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  const match = confirm && password && confirm === password;

  return (
    <AuthLayout aside={ASIDE} width={440}>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <AuthHeading title="Создать аккаунт">
          Уже есть аккаунт? <Link to="/login">Войти</Link>
        </AuthHeading>

        {formError && (
          <Alert tone="error">{formError}</Alert>
        )}

        <div className="auth-row-2">
          <div className="auth-field">
            <label htmlFor="reg-name" className="ui-label">Имя</label>
            <Input id="reg-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="given-name" disabled={loading} autoFocus />
          </div>
          <div className="auth-field">
            <label htmlFor="reg-username" className="ui-label">Логин</label>
            <Input
              id="reg-username"
              value={username}
              onChange={(e) => { setUsername(e.target.value); clear('username'); }}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              disabled={loading}
              aria-invalid={!!errors.username || undefined}
              aria-describedby={errors.username ? 'reg-username-err' : undefined}
            />
            {errors.username && <FieldNote id="reg-username-err">{errors.username}</FieldNote>}
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="reg-email" className="ui-label">Email</label>
          <Input
            id="reg-email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); clear('email'); }}
            autoComplete="email"
            disabled={loading}
            aria-invalid={!!errors.email || undefined}
            aria-describedby={errors.email ? 'reg-email-err' : 'reg-email-hint'}
          />
          {errors.email
            ? <FieldNote id="reg-email-err">{errors.email}</FieldNote>
            : <span id="reg-email-hint" className="ui-field-hint">Нужен, чтобы восстановить пароль</span>}
        </div>

        <div className="auth-field">
          <label htmlFor="reg-password" className="ui-label">Пароль</label>
          <PasswordInput
            id="reg-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); clear('password'); }}
            autoComplete="new-password"
            disabled={loading}
            invalid={!!errors.password}
            aria-invalid={!!errors.password || undefined}
            aria-describedby={['reg-password-req', errors.password && 'reg-password-err'].filter(Boolean).join(' ')}
          />
          {errors.password && <FieldNote id="reg-password-err">{errors.password}</FieldNote>}
          <PasswordStrength id="reg-password-req" password={password} />
        </div>

        <div className="auth-field">
          <label htmlFor="reg-confirm" className="ui-label">Повторите пароль</label>
          <PasswordInput
            id="reg-confirm"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); clear('confirm'); }}
            autoComplete="new-password"
            disabled={loading}
            invalid={!!errors.confirm}
            aria-invalid={!!errors.confirm || undefined}
            aria-describedby={errors.confirm ? 'reg-confirm-err' : (match ? 'reg-confirm-ok' : undefined)}
          />
          {errors.confirm
            ? <FieldNote id="reg-confirm-err">{errors.confirm}</FieldNote>
            : match && <FieldNote id="reg-confirm-ok" tone="ok">Пароли совпадают</FieldNote>}
        </div>

        <Button type="submit" variant="primary" size="lg" block loading={loading}>Создать аккаунт</Button>
      </form>
    </AuthLayout>
  );
};

export default Register;
